import { useQuery } from '@tanstack/react-query';
import { CircleCheck, Clock, FileDiff, Inbox, Mail, Phone, Plus, Ticket } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Dashboard, type TeamMember } from '../lib/api';
import { useAuth } from '../lib/auth';
import { statusLabel, useI18n } from '../lib/i18n';
import { formatDate, formatNumber, formatShortTime } from '../lib/format';
import { signedInName } from '../lib/people';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Metric,
  PageHeader,
  ProgressBar,
  StatusDot,
  statusTone,
} from '../components/ui';

const TASK_STATUSES = ['TO_DO', 'IN_PROGRESS', 'BLOCKED', 'REVIEW', 'DONE', 'CANCELLED'] as const;
const CR_STATUSES = ['NEW', 'IN_REVIEW', 'APPROVED', 'REJECTED', 'DONE'] as const;

/**
 * The client's dashboard: its tickets (total, waiting for us, in progress,
 * finished), the tasks on its projects by status, who of its project team is on
 * shift now, and its change requests.
 */
export function DashboardPage() {
  const { t, locale } = useI18n();
  const { account } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/dashboard'),
    // Who is on shift changes through the day.
    refetchInterval: 60_000,
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <PageHeader
        // A contact signed in with their own sign-in is greeted by name; the company's own sign-in by the company's.
        title={t('dashboard.greeting', { name: signedInName(account, locale) })}
        subtitle={t('dashboard.subtitle')}
        action={
          <div className="flex flex-wrap gap-2">
            {data.canRaiseTickets && (
              <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/tickets/new')}>
                {t('tickets.new')}
              </Button>
            )}
            <Button icon={<FileDiff className="h-4 w-4" />} onClick={() => navigate('/change-requests/new')}>
              {t('crs.new')}
            </Button>
          </div>
        }
      />

      {!data.canRaiseTickets && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('dashboard.ticketsShut')}</p>
        </Card>
      )}

      {/* The headline numbers: a row of stat tiles, each opening its list. */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Metric
          label={t('dashboard.ticketsTotal')}
          value={formatNumber(data.tickets.total, locale)}
          icon={<Ticket className="h-4 w-4" />}
          onClick={() => navigate('/tickets')}
        />
        <Metric
          label={t('dashboard.ticketsOpen')}
          value={formatNumber(data.tickets.open, locale)}
          tone={data.tickets.open > 0 ? 'warning' : 'neutral'}
          icon={<Inbox className="h-4 w-4" />}
          onClick={() => navigate('/tickets?group=open')}
        />
        <Metric
          label={t('dashboard.ticketsInProgress')}
          value={formatNumber(data.tickets.inProgress, locale)}
          icon={<Clock className="h-4 w-4" />}
          onClick={() => navigate('/tickets?group=inProgress')}
        />
        <Metric
          label={t('dashboard.ticketsFinished')}
          value={formatNumber(data.tickets.finished, locale)}
          tone="success"
          icon={<CircleCheck className="h-4 w-4" />}
          onClick={() => navigate('/tickets?group=finished')}
        />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-5 xl:col-span-2">
          <TasksCard tasks={data.tasks} />
          <ProjectsCard projects={data.projects} />
        </div>
        <div className="min-w-0 space-y-5">
          <TeamCard team={data.team} />
          <ChangeRequestsCard requests={data.changeRequests} />
        </div>
      </div>
    </>
  );
}

/**
 * Tasks per status — magnitude across a handful of categories, so one hue,
 * bars from a shared baseline, the count at each bar's tip.
 */
function TasksCard({ tasks }: { tasks: Dashboard['tasks'] }) {
  const { t, locale } = useI18n();
  const max = Math.max(1, ...TASK_STATUSES.map((s) => tasks.byStatus[s] ?? 0));

  return (
    <Card>
      <CardHeader
        title={t('dashboard.tasks')}
        subtitle={t('dashboard.tasksHint')}
        action={
          <Badge tone="neutral" className="tabular">
            {formatNumber(tasks.total, locale)}
          </Badge>
        }
      />
      {tasks.total === 0 ? (
        <p className="text-body-sm text-muted">{t('dashboard.tasksNone')}</p>
      ) : (
        <ul className="space-y-2.5">
          {TASK_STATUSES.map((status) => {
            const count = tasks.byStatus[status] ?? 0;
            const share = tasks.total > 0 ? Math.round((count / tasks.total) * 100) : 0;
            const label = statusLabel(t, 'task', status);
            return (
              <li
                key={status}
                className="grid grid-cols-[7.5rem_1fr_auto] items-center gap-3"
                title={`${label}: ${formatNumber(count, locale)} (${formatNumber(share, locale)}%)`}
              >
                <span className="truncate text-body-sm text-[var(--text-secondary)]">{label}</span>
                <span className="h-2 overflow-hidden rounded-pill bg-[var(--surface-sunken)]" aria-hidden>
                  <span
                    className="block h-full rounded-e-pill bg-primary transition-[width] duration-base ease-precision"
                    style={{ width: `${(count / max) * 100}%` }}
                  />
                </span>
                <span className="min-w-[2.5rem] text-end text-body-sm font-medium tabular text-[var(--text-primary)]">
                  {formatNumber(count, locale)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function ProjectsCard({ projects }: { projects: Dashboard['projects'] }) {
  const { t, locale, localized } = useI18n();
  if (projects.length === 0) return null;

  return (
    <Card>
      <CardHeader title={t('dashboard.projects')} />
      <ul className="divide-y divide-[var(--line)]">
        {projects.map((project) => {
          const percent = project.tasks > 0 ? (project.tasksDone / project.tasks) * 100 : 0;
          return (
            <li key={project.id} className="py-3 first:pt-0 last:pb-0">
              <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                <span className="min-w-0">
                  <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{project.code}</span>{' '}
                  <span className="text-body-sm font-medium text-[var(--text-primary)]">{localized(project)}</span>
                </span>
                <span className="flex items-center gap-2">
                  {project.supportUntil && (
                    <span className="text-caption text-muted">
                      {t('dashboard.supportUntil', { date: formatDate(project.supportUntil, locale) })}
                    </span>
                  )}
                  <Badge tone={statusTone(project.status)}>{statusLabel(t, 'project', project.status)}</Badge>
                </span>
              </div>
              <ProgressBar percent={percent} tone={percent >= 100 ? 'success' : 'brand'} />
              <p className="mt-1 text-caption text-muted tabular">
                {t('dashboard.tasksDone')}: {formatNumber(project.tasksDone, locale)} / {formatNumber(project.tasks, locale)}
              </p>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** "Online employees" — the client's project team on shift now: name, email, mobile. */
function TeamCard({ team }: { team: TeamMember[] }) {
  const { t, locale, localized } = useI18n();

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <StatusDot tone={team.length > 0 ? 'online' : 'offline'} pulse={team.length > 0} />
            {t('dashboard.team')}
          </span>
        }
        subtitle={t('dashboard.teamHint')}
        action={team.length > 0 && <Badge tone="success" className="tabular">{formatNumber(team.length, locale)}</Badge>}
      />
      {team.length === 0 ? (
        <p className="text-body-sm text-muted">{t('dashboard.teamEmpty')}</p>
      ) : (
        <ul className="space-y-3">
          {team.map((person) => (
            <li key={person.id} className="rounded-md border hairline p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
                    {localized(person)}
                  </span>
                  {person.jobTitle && (
                    <span className="block truncate text-caption text-muted">{person.jobTitle}</span>
                  )}
                </span>
                {person.onBreak ? (
                  <Badge tone="warning">{t('dashboard.onBreak')}</Badge>
                ) : (
                  person.onShiftSince && (
                    <span className="text-caption text-faint tabular">
                      {t('dashboard.onShiftSince', { time: formatShortTime(person.onShiftSince, locale) })}
                    </span>
                  )
                )}
              </div>
              <div className="mt-2 space-y-1">
                <a
                  href={`mailto:${person.email}`}
                  className="flex items-center gap-2 text-body-sm text-primary-600 dark:text-primary-400 hover:underline"
                  title={t('common.email')}
                >
                  <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="sr-only">{t('common.email')}: </span>
                  <bdi className="truncate" dir="ltr">{person.email}</bdi>
                </a>
                {person.phone && (
                  <a
                    href={`tel:${person.phone.replace(/[^\d+]/g, '')}`}
                    className="flex items-center gap-2 text-body-sm text-primary-600 dark:text-primary-400 hover:underline"
                    title={t('common.mobile')}
                  >
                    <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="sr-only">{t('common.mobile')}: </span>
                    <bdi dir="ltr">{person.phone}</bdi>
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ChangeRequestsCard({ requests }: { requests: Dashboard['changeRequests'] }) {
  const { t, locale } = useI18n();

  return (
    <Card>
      <CardHeader
        title={t('dashboard.changeRequests')}
        action={
          <Link to="/change-requests" className="text-body-sm text-primary-600 dark:text-primary-400 hover:underline">
            {t('dashboard.seeAll')}
          </Link>
        }
      />
      {requests.total === 0 ? (
        <EmptyState icon={<FileDiff className="h-7 w-7" />} title={t('crs.empty')} hint={t('crs.emptyHint')} />
      ) : (
        <>
          <p className="text-body-sm text-muted">
            {t('dashboard.changeRequestsOpen')}:{' '}
            <span className="font-medium tabular text-[var(--text-primary)]">{formatNumber(requests.open, locale)}</span>
          </p>
          <dl className="mt-3 divide-y divide-[var(--line)]">
            {CR_STATUSES.map((status) => (
              <div key={status} className="flex items-center justify-between py-1.5">
                <dt>
                  <Badge tone={statusTone(status)}>{statusLabel(t, 'cr', status)}</Badge>
                </dt>
                <dd className="text-body-sm font-medium tabular text-[var(--text-primary)]">
                  {formatNumber(requests.byStatus[status] ?? 0, locale)}
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </Card>
  );
}
